import React, { useState } from "react";
import { InertiaLink } from "@inertiajs/inertia-react";
import Logo from "@/components/elements/Logo";
import Modal from "@/components/Modal";
import api from "@/services/api";

/**
 * Sidepanel component that provides navigation and step management interface
 * Displays logo, navigation steps with substeps, and product deletion functionality
 * Supports both validated and direct navigation modes
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Additional content to render in the sidebar
 * @param {Array} props.steps - Array of navigation steps with substeps
 * @param {Array} props.choices - Available choices/options (used for conditional rendering)
 * @param {Function} props.clickStep - Function to handle step clicks when validation is enabled
 * @param {Object} props.product - Current product data including session and product IDs
 * @param {boolean} props.validates - Whether to use validated navigation (default: false)
 */
const Sidepanel = ({
    children,
    steps,
    choices,
    clickStep,
    product,
    validates = false,
}) => {

    // State for controlling the delete confirmation modal
    const [deleteOpen, setDeleteOpen] = useState(false);

    /**
     * Show the product deletion confirmation modal
     */
    function askForDeletion() {
        setDeleteOpen(true);
    }

    /**
     * Delete the current product via API call
     * Redirects to URL returned by server after successful deletion
     */
    function deleteProduct() {
        api.delete(
            `/api/product/${product.session_id}/${product.product_id}`,
        ).then((response) => {
            window.location.href = response.data.url;
        });
    }

    return (
        <div className="sidepanel d-flex flex-column background-sidebar-gray">

            {/* Application logo at the top */}
            <Logo />

            {/* Navigation steps section */}
            {steps && steps.length > 0 && (
                <div className="steps">
                    {steps.map((step, key) =>
                        step.type ? (
                            // Header/section step (non-clickable)
                            <div className="step no-hover fontbold" key={key}>
                                {step.name}
                            </div>
                        ) : (
                            // Regular navigation step with optional substeps
                            <div key={key}>
                                {validates ? (
                                    // Validated navigation mode - use click handler
                                    <a
                                        href="#"
                                        className="step headquestion"
                                        name={step.name}
                                        onClick={(e) =>
                                            clickStep(e, step.route)
                                        }
                                    >
                                        {step.name}
                                    </a>
                                ) : (
                                    // Direct navigation mode - use Inertia links
                                    <InertiaLink
                                        className="step headquestion"
                                        name={step.name}
                                        href={step.route}
                                    >
                                        {step.name}
                                    </InertiaLink>
                                )}

                                {/* Render substeps if the main step is active and has substeps */}
                                {step.active && step.substeps.length > 0 ? (
                                    <>
                                        {step.substeps.map((substep, key) =>
                                            validates ? (
                                                // Validated navigation for substeps
                                                <a
                                                    href="#"
                                                    key={key}
                                                    className={`step ${substep.active ? "active" : ""}`}
                                                    name={substep.name}
                                                    onClick={(e) =>
                                                        clickStep(
                                                            e,
                                                            substep.route,
                                                        )
                                                    }
                                                >
                                                    {substep.name}
                                                </a>
                                            ) : (
                                                // Direct navigation for substeps
                                                <InertiaLink
                                                    key={key}
                                                    name={substep.name}
                                                    className={`step ${substep.active ? "active" : ""}`}
                                                    href={substep.route}
                                                >
                                                    {substep.name}
                                                </InertiaLink>
                                            ),
                                        )}
                                    </>
                                ) : (
                                    <></>
                                )}
                            </div>
                        ),
                    )}
                </div>
            )}

            {/* Additional content and product deletion section */}
            <div className="steps">
                {children}

                {/* Show delete button if there are choices or steps available */}
                {(choices || (steps && steps.length > 0)) && (
                    <div className={"text-center mt-3"}>
                        <a
                            onClick={askForDeletion}
                            className="navigationbutton d-inline-flex align-items-center"
                        >
                            {/* Delete icon */}
                            <svg
                                className="mr-2"
                                fill="currentColor"
                                height="18"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            Product verwijderen
                        </a>
                    </div>
                )}
            </div>

            {/* Delete confirmation modal */}
            <Modal
                open={deleteOpen}
                title={"Verwijderen"}
                content={"Wil je dit product verwijderen?"}
                savetitle="Verwijder"
                buttonclass="btn-orange"
                canceltitle="Annuleer"
                savefunction={deleteProduct}
                updateOpen={() => setDeleteOpen(false)}
            />
        </div>
    );
};

export default Sidepanel;
